'use client';

import { type ReactElement, useMemo } from 'react';
import { Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import {
    type WasLoggedUserAddressChangedStruct,
    AccountDrawerType,
    PageType,
    usePageLayout,
    useLocalStorage,
    useCart,
    useOptimizely,
    useDecisions,
    cartHasVetDietItem,
    cartHasVetRxItem,
} from '@libs/common-next';
import type { LoyaltySignIn } from '@libs/contentful';
import { LoginEvents } from '@libs/analytics';

import { InterstitialSignIn } from '../InterstitialSignIn';
import { generateVelocityCreateAccountUrl } from './generateCreateAccountUrl';

import './SignInDrawer.scss';
import { WAS_LOGGED_USER_ADDRESS_CHANGED } from '@libs/core';
import type { SignInFlags } from './SignInDrawerContainer';
import { useOfferCardActions } from '@libs/shared-ui';

export function SignInDrawer({
    data,
    flags,
}: Readonly<{
    data?: LoyaltySignIn;
    flags?: SignInFlags;
}>): ReactElement | null {
    const componentClassName = 'sign-in-drawer';

    const { cart } = useCart();

    const cartHasRx = cart && flags?.isVetSourceRxEnabled && cartHasVetRxItem(cart);
    const cartHasVetDiet = cart && flags?.isVetSourceDietEnabled && cartHasVetDietItem(cart);
    const showRxMessage = cartHasRx || cartHasVetDiet;

    const { accountDrawersOpen, removeAccountDrawers, signInDrawerProps, pageType, openAccountDrawer } =
        usePageLayout();
    const { drawerOpen } = useOfferCardActions();
    const { context } = useOptimizely();
    const abTestFlag = useDecisions('exp_loyalty_off');
    const [_, setWasLoggedUserAddressChangedStruct] = useLocalStorage<WasLoggedUserAddressChangedStruct>(
        WAS_LOGGED_USER_ADDRESS_CHANGED
    );

    const createAccountHref = useMemo(() => {
        const { productToAddToWishlist, offerIdToActivate } = signInDrawerProps ?? {};

        switch (pageType) {
            case PageType.PLP:
            case PageType.PDP:
                if (productToAddToWishlist) {
                    return generateVelocityCreateAccountUrl('wishlist', productToAddToWishlist as string);
                }
                return undefined;
            case PageType.HCP:
                if (offerIdToActivate) {
                    return generateVelocityCreateAccountUrl('offerActivationId', offerIdToActivate as string);
                }
                return undefined;
            default:
                return undefined;
        }
    }, [pageType, signInDrawerProps]);

    const benefitItems = data?.items?.[0]?.fields?.items || [];
    const isActive = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN);

    return (
        <Drawer
            data-testid="sign-in-drawer"
            align="right"
            isActive={isActive}
            onClose={() => {
                removeAccountDrawers([AccountDrawerType.SIGN_IN]);
                if (abTestFlag && window.localStorage.getItem('opt-ex-offer-id')) {
                    window.localStorage.removeItem('opt-ex-offer-id');
                    openAccountDrawer(AccountDrawerType.OFFERS);
                }
            }}
            ariaLabelledBy="drawer-heading-1"
            className={componentClassName}
        >
            <Drawer.Header>
                {data?.previewMeta}
                <Heading data-testid="sign-in-drawer-heading-1" id="drawer-heading-1" tagName="h5" size="title">
                    {data?.signInTitle}
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                {showRxMessage && (
                    <>
                        <TextPassage>
                            Pharmacy and Vet Diet orders require an account to checkout. Please sign in or create an
                            account to complete your purchase.
                        </TextPassage>
                        <br />
                    </>
                )}
                <div className={`${componentClassName}__body`}>
                    <InterstitialSignIn
                        itemsTitle={data?.title}
                        benefitItems={benefitItems}
                        isInDrawer
                        createAccountHref={createAccountHref}
                        location={LoginEvents.SIGN_IN_DRAWER}
                        onSuccessfulSignIn={() => {
                            if (abTestFlag) {
                                context?.trackEvent('sign_ins');
                                if (drawerOpen || window.localStorage.getItem('opt-ex-offer-id')) {
                                    context?.trackEvent('fly_out_sign_ins');
                                }
                            } else {
                                context?.trackEvent('sign_ins');
                            }
                            setWasLoggedUserAddressChangedStruct(undefined);
                        }}
                        displayAutoshipDescription
                    />
                </div>
            </Drawer.Body>
        </Drawer>
    );
}
